import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { TextAreaContainer } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
  ModalSheetHeader,
} from 'components/ModalSheet'
import JobViewModel from '../JobViewModel'
import { match } from 'utils/pattern-match-util'
import styles from './UpdateJobScopeDialog.sass'
import { useActivation } from 'hooks/mobx-hooks'
import JobScopeCustomItem from './ListItems/JobScopeCustomItem'
import JobScopeInventoryItem from './ListItems/JobScopeInventoryItem'
import ManageItemsViewModel from './ManageItemsViewModel'
import UpdateJobScopeViewModel, {
  UpdateJobScopeState,
} from './UpdateJobScopeViewModel'

interface UpdateJobScopeDialogProps {
  vm: JobViewModel
}

export default observer(function UpdateJobScopeDialog({
  vm,
}: UpdateJobScopeDialogProps) {
  const updateJobScopeVM = vm.updateJobScopeVM
  const disabled = updateJobScopeVM.submit.pending || updateJobScopeVM.disabled
  useActivation(updateJobScopeVM)

  return (
    <ModalSheet
      showing={updateJobScopeVM.showing}
      onRequestClose={updateJobScopeVM.close}
      disableCloseOnOutsideClick={updateJobScopeVM.submit.pending}
      disableCloseOnEsc={updateJobScopeVM.submit.pending}
    >
      {match(updateJobScopeVM.state, {
        [UpdateJobScopeState.UpdateJobScope]: () => (
          <>
            <UpdateJobScopeHeading title="Update job scope">
              <ItemHeadings vm={updateJobScopeVM} />
            </UpdateJobScopeHeading>

            <GreyContainer>
              <ModalSheetContent>
                <div className={styles.itemsContainer}>
                  {updateJobScopeVM.isNoServiceItemsOnJob && (
                    <div className={styles.itemsEmptyState}>
                      There are no service items on this job.
                    </div>
                  )}

                  {updateJobScopeVM.selectedInventoryItems
                    .filter((i) => i.canShow)
                    .map((item) => {
                      return (
                        <JobScopeInventoryItem
                          key={item.versionId}
                          item={item}
                          dataTestId={item.id}
                        />
                      )
                    })}
                  {updateJobScopeVM.selectedCustomItems.map((item) => {
                    return (
                      <JobScopeCustomItem
                        key={item.id}
                        item={item}
                        remove={updateJobScopeVM.removeCustomItem}
                        dataTestId={item.name}
                      />
                    )
                  })}
                </div>
              </ModalSheetContent>
            </GreyContainer>

            <ModalSheetFooter>
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={updateJobScopeVM.manageItemsVM.show}
                  dataTest="job-scope-update-add-more-button"
                >
                  Add More
                </Button>
              </ModalSheetFooterButtonGroup>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={updateJobScopeVM.close}
                  dataTest="job-scope-update-close-button"
                >
                  Close
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  onClick={() =>
                    updateJobScopeVM.setState(UpdateJobScopeState.SpecifyReason)
                  }
                  dataTest="job-scope-update-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [UpdateJobScopeState.SpecifyReason]: () => (
          <>
            <UpdateJobScopeHeading title="Confirm updates" />

            <ModalSheetContent expandable>
              <ModalSheetContentBlock expanded>
                <TextAreaContainer
                  label="Reason"
                  model={updateJobScopeVM}
                  field="reason"
                  name="reason"
                  data-test="job-scope-update-reason-input"
                  help={`${updateJobScopeVM.reasonCharsRemaining} characters remaining.`}
                  focusOnError
                  placeholder="The reason for updating the job scope. Optional."
                  error={updateJobScopeVM.validation.getError('reason')}
                />
              </ModalSheetContentBlock>
            </ModalSheetContent>

            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    updateJobScopeVM.setState(
                      UpdateJobScopeState.UpdateJobScope
                    )
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  loading={updateJobScopeVM.submit.pending}
                  onClick={updateJobScopeVM.submit}
                  dataTest="job-scope-update-submit-button"
                >
                  Submit
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [UpdateJobScopeState.Success]: () => (
          <>
            <ModalSheetHeader withCloseButton />
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentIllustration
                  src={require('assets/images/img_welcome.svg')}
                />
                <ModalSheetContentTitle dataTest="job-scope-update-success">
                  Job updates submitted
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  The job has been updated and all changes were applied to the
                  job.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={updateJobScopeVM.close}
                  dataTest="job-scope-update-success-close-button"
                >
                  Close
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [UpdateJobScopeState.Failed]: () => (
          <>
            <ModalSheetHeader withCloseButton />
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Unable to update job scope
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {updateJobScopeVM.error ? (
                    updateJobScopeVM.error
                  ) : (
                    <p>
                      Failure occurred while trying to update the job scope.
                      Please try again.
                    </p>
                  )}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button disabled={disabled} onClick={updateJobScopeVM.close}>
                  Cancel
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
      <ManageItemsViewModel vm={updateJobScopeVM.manageItemsVM} />
    </ModalSheet>
  )
})

const UpdateJobScopeHeading = observer(function UpdateJobScopeHeading({
  title,
  children,
}: {
  title: string
  children?: React.ReactNode
}) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.closeButtonContainer}>
            <ModalSheetHeaderCloseButton />
          </div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
})

const ItemHeadings = observer(function ItemHeadings({
  vm,
}: {
  vm: UpdateJobScopeViewModel
}) {
  return (
    <div className={styles.listItem}>
      <span>Service item</span>

      {vm.state === UpdateJobScopeState.UpdateJobScope && (
        <span className={styles.listItemCurrent}>
          Current &nbsp;
          <Tooltip
            content={'Current quantity for each item on the job.'}
            placement="top"
          >
            <Icon name="info" size={18} color="var(--blue400)" />
          </Tooltip>
        </span>
      )}
      <span
        className={
          vm.state === UpdateJobScopeState.SpecifyReason
            ? styles.listItemNewTotalQuantitesState
            : styles.listItemNewTotal
        }
      >
        New total &nbsp;
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
    </div>
  )
})

const GreyContainer = observer(function GreyContainer({
  children,
  centerContent = false,
}: {
  children: React.ReactNode
  centerContent?: boolean
}) {
  return (
    <div
      className={cx(
        styles.greyBackround,
        centerContent && styles.centerContent
      )}
    >
      {children}
    </div>
  )
})
